import { HttpClient, RequestParams } from "../../utils/api/http-client";
import {
    IAddOrderProductRequest,
    IUpdateOrderProductRequest,
    IRemoveOrderProductRequest,
    IOrderProductBatchAllocationRequest,
    IOrderProductResponse,
    IOrderProductBulkResponse,
} from "../../models/order-product/types";

const stripUndefined = <T extends Record<string, any>>(value: T): T =>
    Object.entries(value).reduce((result, [key, entryValue]) => {
        if (entryValue !== undefined) {
            result[key as keyof T] = entryValue as T[keyof T];
        }
        return result;
    }, {} as T);

const buildAddProductsBody = (data: IAddOrderProductRequest) => ({
    storeId: data.storeId,
    products: data.products.map((product) => {
        const quantityByUnit =
            product.quantityByUnit ??
            product.availableWeight ??
            product.available_weight;

        return stripUndefined({
            productId: product.productId ?? product.product_id,
            quantityByUnit,
            count: quantityByUnit === undefined ? product.count : undefined,
            unitPartitionId: product.unitPartitionId ?? product.unit_partition_id,
        });
    }),
});

const buildUpdateProductBody = (data: IUpdateOrderProductRequest) =>
    (() => {
        const targetReservedQuantityByUnit =
            data.targetReservedQuantityByUnit ?? data.availableWeight;
        return stripUndefined({
            productOrderId: data.productOrderId,
            targetReservedQuantityByUnit,
            targetQuantityByUnit: data.targetQuantityByUnit,
            count:
                targetReservedQuantityByUnit === undefined &&
                data.targetQuantityByUnit === undefined
                    ? data.count
                    : undefined,
            unitPartitionId: data.unitPartitionId,
            isAccepted: data.isAccepted,
            storeId: data.storeId,
        });
    })();

const buildBatchAllocationBody = (data: IOrderProductBatchAllocationRequest) =>
    stripUndefined({
        productOrderId: data.productOrderId,
        batchId: data.batchId,
        targetQuantityByUnit:
            data.targetQuantityByUnit ?? data.quantityByUnit ?? data.quantity,
    });

class OrderProductService<SecurityDataType = unknown> {
    http: HttpClient<SecurityDataType>;

    constructor(http: HttpClient<SecurityDataType>) {
        this.http = http;
    }

    /**
     * Add multiple products from the same store to an order
     * @param orderId - ID of the order
     * @param data - Store ID and array of products to add
     */
    addProducts = (
        orderId: number,
        data: IAddOrderProductRequest,
        params?: RequestParams
    ) =>
        this.http.request<IOrderProductBulkResponse>({
            path: `/admin/order/${orderId}/product`,
            method: "POST",
            body: buildAddProductsBody(data),
            ...params,
        });

    /**
     * Update an existing product in an order
     * @param data - Product order ID and fields to update
     */
    updateProduct = (
        data: IUpdateOrderProductRequest,
        params?: RequestParams
    ) =>
        this.http.request<IOrderProductResponse>({
            path: `/admin/order/product`,
            method: "PUT",
            body: buildUpdateProductBody(data),
            ...params,
        });

    /**
     * Remove a product from an order
     * Note: Inventory is NOT returned when removing
     * @param data - Product order ID to remove
     */
    removeProduct = (
        data: IRemoveOrderProductRequest,
        params?: RequestParams
    ) =>
        this.http.request<{ success: boolean; message: string }>({
            path: `/admin/order/product`,
            method: "DELETE",
            body: data,
            ...params,
        });

    addBatchAllocation = (
        data: IOrderProductBatchAllocationRequest,
        params?: RequestParams
    ) =>
        this.http.request<IOrderProductResponse>({
            path: `/admin/order/product/batch`,
            method: "PATCH",
            body: buildBatchAllocationBody(data),
            ...params,
        });

    updateBatchAllocation = (
        data: IOrderProductBatchAllocationRequest,
        params?: RequestParams
    ) =>
        this.http.request<IOrderProductResponse>({
            path: `/admin/order/product/batch`,
            method: "PATCH",
            body: buildBatchAllocationBody(data),
            ...params,
        });
}

export default OrderProductService;
